import type { Client } from '../types';

export function sameClientId(
  a: number | string | null | undefined,
  b: number | string | null | undefined,
): boolean {
  if (a == null || b == null || a === '' || b === '') return false;
  const na = Number(a);
  const nb = Number(b);
  return Number.isFinite(na) && Number.isFinite(nb) && na === nb;
}

export function findClientById(
  clients: Client[],
  clientId: number | string | null | undefined,
): Client | undefined {
  return clients.find(c => sameClientId(c.client_id, clientId));
}

export function getClientDisplayName(
  clients: Client[],
  clientId: number | string | null | undefined,
): string {
  const client = findClientById(clients, clientId);
  if (client) {
    const name = `${client.first_name ?? ''} ${client.last_name ?? ''}`.trim();
    if (name) return name;
  }
  if (clientId != null && clientId !== '') return `Client ${clientId}`;
  return 'Unknown Client';
}

export async function loadClientsForRecords(
  records: { client_id: number | string; provider_id?: number | string }[],
  options: {
    providerId: number | null;
    listByProvider: (providerId: number | string, limit?: number) => Promise<Client[]>;
  },
): Promise<Client[]> {
  const clientIds = Array.from(
    new Set(records.map(r => Number(r.client_id)).filter(id => Number.isFinite(id) && id > 0)),
  );
  if (clientIds.length === 0) return [];

  const providerIds = options.providerId
    ? [options.providerId]
    : Array.from(
        new Set(
          records
            .map(r => Number(r.provider_id))
            .filter(id => Number.isFinite(id) && id > 0),
        ),
      );

  const lists = await Promise.all(
    providerIds.map(pid => options.listByProvider(pid, 1000).catch(() => [] as Client[])),
  );

  const byId = new Map<number, Client>();
  lists.flat().forEach(c => {
    const id = Number(c.client_id);
    if (clientIds.includes(id)) byId.set(id, c);
  });
  return Array.from(byId.values());
}
